/**
 * SavedSignatureCard (/profile) — set/replace/remove the user's reusable
 * e-signature AND initials (draw / type-in-font / upload). Both are stored as
 * small data URLs on the own user doc via signatureSaveProfile; offered as the
 * default when a document is signed. Two independent blocks so people can set
 * either or both — mirrors the 2-part setup progress shown on /signatures.
 */
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { signatureInitials } from '@daaily/shared';
import { db } from '../lib/firebase';
import { useProfile } from '../profile/ProfileProvider';
import { SignaturePad } from './SignaturePad';
import { InitialsCapture } from './InitialsCapture';
import { paperStyle, type SignatureFont } from './signatureFonts';
import { callSignatureSaveProfile, stripDataUrl } from './lib';

export function SavedSignatureCard() {
  const { state } = useProfile();
  const email = state.status === 'ready' ? state.user.email.toLowerCase() : '';
  const displayName = state.status === 'ready' ? state.user.displayName : '';
  const initials = signatureInitials(displayName || '');

  const [savedSig, setSavedSig] = useState<string | null>(null);
  const [savedIni, setSavedIni] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Signature editor state
  const [editSig, setEditSig] = useState(false);
  const [sigDraft, setSigDraft] = useState<string | null>(null);
  const [sigFont, setSigFont] = useState<SignatureFont | null>(null);
  const [sigBusy, setSigBusy] = useState(false);
  // Initials editor state
  const [editIni, setEditIni] = useState(false);
  const [iniDraft, setIniDraft] = useState<string | null>(null);
  const [iniBusy, setIniBusy] = useState(false);

  useEffect(() => {
    if (!email) return;
    getDoc(doc(db, 'users', email))
      .then((s) => {
        setSavedSig((s.get('savedSignature') as string) ?? null);
        setSavedIni((s.get('savedInitials') as string) ?? null);
      })
      .catch(() => undefined);
  }, [email]);

  const saveSig = async () => {
    if (!sigDraft) return;
    setSigBusy(true); setErr(null);
    try {
      await callSignatureSaveProfile({ imageBase64: stripDataUrl(sigDraft), kind: 'signature' });
      setSavedSig(sigDraft); setEditSig(false);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setSigBusy(false); }
  };
  const removeSig = async () => {
    setSigBusy(true); setErr(null);
    try { await callSignatureSaveProfile({ imageBase64: null, kind: 'signature' }); setSavedSig(null); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove.'); } finally { setSigBusy(false); }
  };
  const saveIni = async () => {
    if (!iniDraft) return;
    setIniBusy(true); setErr(null);
    try {
      await callSignatureSaveProfile({ imageBase64: stripDataUrl(iniDraft), kind: 'initials' });
      setSavedIni(iniDraft); setEditIni(false);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setIniBusy(false); }
  };
  const removeIni = async () => {
    setIniBusy(true); setErr(null);
    try { await callSignatureSaveProfile({ imageBase64: null, kind: 'initials' }); setSavedIni(null); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove.'); } finally { setIniBusy(false); }
  };

  const doneCount = (savedSig ? 1 : 0) + (savedIni ? 1 : 0);

  return (
    <div className="rounded-lg border border-line bg-surface p-4 mt-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-sm font-medium text-fg">My signature &amp; initials</div>
        <span className="text-2xs text-fg-faint">{doneCount}/2 set up</span>
      </div>
      <p className="text-xs text-fg-faint mb-3">
        Set these once and reuse them whenever a document is sent to you ("Use saved").
      </p>
      {err && <p className="text-sm text-danger mb-2">{err}</p>}

      {/* Signature */}
      <div className="border-t border-line pt-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-sm text-fg-muted">Signature</div>
          {!editSig && (
            <div className="flex gap-3">
              <button type="button" className="text-xs text-link underline" onClick={() => { setEditSig(true); setSigDraft(null); }}>
                {savedSig ? 'Replace' : 'Set signature'}
              </button>
              {savedSig && <button type="button" className="text-xs text-danger underline" disabled={sigBusy} onClick={removeSig}>Remove</button>}
            </div>
          )}
        </div>
        {editSig ? (
          <div className="space-y-3">
            <SignaturePad onChange={setSigDraft} typeName={displayName} onFontChange={setSigFont} />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary !py-1.5 !font-normal" onClick={() => setEditSig(false)}>Close</button>
              <button type="button" className="btn-primary !py-1.5 !font-normal" disabled={sigBusy || !sigDraft} onClick={saveSig}>
                {sigBusy ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </div>
        ) : savedSig ? (
          <img src={savedSig} alt="Saved signature" className="max-h-20 object-contain rounded border border-line p-2" style={paperStyle} />
        ) : (
          <p className="text-sm text-fg-faint">No signature saved yet.</p>
        )}
      </div>

      {/* Initials */}
      <div className="border-t border-line pt-3 mt-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-sm text-fg-muted">Initials</div>
          {!editIni && (
            <div className="flex gap-3">
              <button type="button" className="text-xs text-link underline" onClick={() => { setEditIni(true); setIniDraft(null); }}>
                {savedIni ? 'Replace' : 'Set initials'}
              </button>
              {savedIni && <button type="button" className="text-xs text-danger underline" disabled={iniBusy} onClick={removeIni}>Remove</button>}
            </div>
          )}
        </div>
        {editIni ? (
          <div className="space-y-3">
            <InitialsCapture initialsText={initials} signatureFont={sigFont} onChange={setIniDraft} />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary !py-1.5 !font-normal" onClick={() => setEditIni(false)}>Close</button>
              <button type="button" className="btn-primary !py-1.5 !font-normal" disabled={iniBusy || !iniDraft} onClick={saveIni}>
                {iniBusy ? 'Saving…' : 'Save initials'}
              </button>
            </div>
          </div>
        ) : savedIni ? (
          <img src={savedIni} alt="Saved initials" className="max-h-16 object-contain rounded border border-line p-2" style={paperStyle} />
        ) : (
          <p className="text-sm text-fg-faint">No initials saved yet.</p>
        )}
      </div>

    </div>
  );
}
